/**
 * What makes two notifications THE SAME notification. One definition, used by the extension,
 * `apps/web` and `client-node`'s archive.
 *
 * **The rule: never dedup notifications on `msgId`.** It is a TRANSPORT id — the phone mints a
 * fresh UUID per relay — so it names one delivery, never one notification. `createSeenTracker`
 * in this directory is msgId-keyed and is a different tool for a different job (a replayed
 * delivery), not a substitute for this.
 *
 * ---
 *
 * WHY, if you need it. Two shipped Android paths re-relay a notification under a new id, so
 * msgId-keyed dedup is blind to both:
 *
 *   - **Listener rebind** — `CaptureSessionResetter.onConnected()` clears the phone's dedup map
 *     and up to 20 on-screen notifications are re-relayed with fresh ids. Fires on boot,
 *     process death, app update, notification-access toggle.
 *   - **Adding a device** — `relayActiveNotificationToPartner` mints a fresh id PER PARTNER.
 *
 * A content key catches both because `timestamp` is the phone's `sbn.postTime`, unchanged for a
 * notification that merely lingered. Conversations never reach either path
 * (`ActiveNotificationSynchronizer` reseeds `conversationTracker` first) and carry their own
 * `messageHash` dedup regardless.
 *
 * WHY IT IS SHARED. The three surfaces each implemented this separately and had already
 * drifted — the archive's key included `senderDeviceId`, the other two did not, and no file
 * mentioned the others. Changing the field list here changes what every surface treats as a
 * duplicate at once; the suite pins each field for that reason.
 */

/**
 * The fields that constitute a notification's identity.
 *
 * Structurally satisfied by both `DecryptedNotification` (the PC surfaces' stored card) and
 * `ArchiveEntry` (the NDJSON archive record), which is why this takes a shape rather than
 * either concrete type — `client-core` must not depend on `client-node`.
 */
export interface NotificationContentIdentity {
  appPackage: string;
  title: string;
  body: string;
  /** The phone's `sbn.postTime` / message timestamp — NOT a relay-time clock. */
  timestamp: number;
  /** Present on archive entries, absent on the PC surfaces' stored cards. */
  senderDeviceId?: string | undefined;
}

/**
 * A stable string key for content identity.
 *
 * ⚠️ A JSON TUPLE, NOT A JOINED STRING. Any single-character separator is forgeable from
 * notification CONTENT — a body containing it lets one entry impersonate a different field
 * split of another — and even NUL is reachable through a JSON escape. Quoting each field makes
 * the encoding unambiguous, and keeps `timestamp` a number rather than coercing it.
 *
 * ⚠️ `title` AND `senderDeviceId` ARE IN THE KEY, so the trade-off runs toward SHOWING.
 * Dropping `title` would catch one more case (a conversation titled by its last sender, which
 * drifts as the thread grows) but would also collapse "Alice: ok" and "Bob: ok" posted in the
 * same millisecond. **A visible duplicate costs a slot; a hidden message is invisible data
 * loss.** Absent `senderDeviceId` normalises to `null`, so omitted and `undefined` agree.
 *
 * Safe against genuinely repeated notifications: `timestamp` comes from the phone, so two real
 * ones collide only if the same app posted identical text in the same millisecond.
 */
export function notificationContentKey(n: NotificationContentIdentity): string {
  return JSON.stringify([n.appPackage, n.timestamp, n.title, n.body, n.senderDeviceId ?? null]);
}

/**
 * Whether two notifications describe the same underlying notification.
 *
 * Defined in terms of `notificationContentKey` rather than comparing fields directly, so the
 * two cannot drift: a field added to the key is automatically part of equality. That is the
 * whole failure this module exists to prevent, so it is not worth saving the string builds.
 */
export function isSameNotificationContent(
  a: NotificationContentIdentity,
  b: NotificationContentIdentity
): boolean {
  return notificationContentKey(a) === notificationContentKey(b);
}
